/**
 * Shared schemas for the Post-Session Review feature.
 *
 * Used by:
 *   - apps/web SessionReview/* to type the tabs' state
 *   - apps/mobile (player post-session screen) to validate self-feedback
 *     payloads before writing
 *   - packages/core/reviewAggregator to roll grades → corner deltas
 *   - packages/brain draft tools (draft_drill_grade, draft_player_grade,
 *     draft_session_review) to type their AI-proposed payloads
 *
 * Row shapes mirror migration 00022_session_review.sql.
 */
import { z } from 'zod';

export const SessionReviewStatusSchema = z.enum(['draft', 'complete']);
export type SessionReviewStatus = z.infer<typeof SessionReviewStatusSchema>;

export const DrillSentimentSchema = z.enum(['worked', 'mixed', 'struggled']);
export type DrillSentiment = z.infer<typeof DrillSentimentSchema>;

// ---------- attendance ----------

export const SessionAttendanceSchema = z.object({
  id: z.string().uuid().optional(),
  session_id: z.string().uuid(),
  player_id: z.string().uuid(),
  present: z.boolean(),
  reason: z.string().max(200).nullable().optional(),
});
export type SessionAttendance = z.infer<typeof SessionAttendanceSchema>;

// ---------- session-level review ----------

export const SessionReviewSchema = z.object({
  id: z.string().uuid().optional(),
  session_id: z.string().uuid(),
  coach_id: z.string().uuid(),
  status: SessionReviewStatusSchema,
  notes_working: z.string().nullable().optional(),
  notes_issues: z.string().nullable().optional(),
  /** Fed into generationContext for the next session. */
  next_session_tweaks: z.string().nullable().optional(),
  /** Populated by the Brain after the coach completes the review. */
  ai_narrative: z.string().nullable().optional(),
  completed_at: z.string().datetime().nullable().optional(),
});
export type SessionReview = z.infer<typeof SessionReviewSchema>;

// ---------- drill grade ----------

export const DrillGradeSchema = z.object({
  id: z.string().uuid().optional(),
  session_review_id: z.string().uuid(),
  session_drill_id: z.string().uuid(),
  /** 1-5 quantitative. Optional — sentiment is the primary signal. */
  grade: z.number().int().min(1).max(5).nullable().optional(),
  sentiment: DrillSentimentSchema.nullable().optional(),
  coach_notes: z.string().max(1000).nullable().optional(),
});
export type DrillGrade = z.infer<typeof DrillGradeSchema>;

// ---------- player self-feedback (mobile) ----------

export const PlayerSelfFeedbackSchema = z.object({
  id: z.string().uuid().optional(),
  session_id: z.string().uuid(),
  player_id: z.string().uuid(),
  wellness_log_id: z.string().uuid().nullable().optional(),
  free_text: z.string().max(500).nullable().optional(),
  submitted_at: z.string().datetime().optional(),
});
export type PlayerSelfFeedback = z.infer<typeof PlayerSelfFeedbackSchema>;

// ---------- per-session player grade rollup (view) ----------

export const SessionPlayerGradeRowSchema = z.object({
  session_id: z.string().uuid(),
  player_id: z.string().uuid(),
  corner_id: z.string().uuid(),
  sub_corner_id: z.string().uuid().nullable(),
  avg_score: z.number(),
  last_updated: z.string().datetime(),
  revealed: z.boolean(),
});
export type SessionPlayerGradeRow = z.infer<typeof SessionPlayerGradeRowSchema>;

// ---------- draft payloads (Brain tools → DraftCard approval) ----------

/** AI-proposed drill grade before coach approval. */
export const DraftDrillGradeSchema = z.object({
  session_drill_id: z.string().uuid(),
  /** The grade the Brain wants to set (1-5 or null). */
  grade: z.number().int().min(1).max(5).nullable(),
  sentiment: DrillSentimentSchema,
  reasoning: z.string().max(500),
});
export type DraftDrillGrade = z.infer<typeof DraftDrillGradeSchema>;

/** AI-proposed per-corner grades for one player across the session. */
export const DraftPlayerGradeSchema = z.object({
  player_id: z.string().uuid(),
  corner_scores: z.record(z.string(), z.number().int().min(0).max(100)),
  /** Short reasoning the coach can edit before committing. */
  reasoning: z.string().max(500),
});
export type DraftPlayerGrade = z.infer<typeof DraftPlayerGradeSchema>;

/** AI-proposed session-level review text. */
export const DraftSessionReviewSchema = z.object({
  notes_working: z.string().max(800),
  notes_issues: z.string().max(800),
  next_session_tweaks: z.string().max(800),
  ai_narrative: z.string().max(1200),
});
export type DraftSessionReview = z.infer<typeof DraftSessionReviewSchema>;
